import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { NewlyAddedPanel, CurrentMovablePanel, DeletedPanel, PanelSlice } from './types';
import { initializeState } from './util';

const initialState: { data: PanelSlice } = {
	data: initializeState()
};

function resetUnfinalizedState(state: PanelSlice) {
	state.enableAddPanel = false;
	state.newlyAddedPanels = [];
	state.enablePanelMove = false;
	state.currentMovablePanels = [];
	state.deleteIndividualPanelMode = false;
	state.deletedPanels = [];
	state.currentIndex = 0;
	state.panelHistory = [];
	state.enableMultipleAddPanel = false;
	state.tempPanelsGroup = [];
	state.selectedMovablePanels = [];
	state.isInvalidPanelOperation = false;
}

type PanelHistoryEntry = PanelSlice['panelHistory'][number];
type HistoryCompare = {
	newly?: boolean;
	deleted?: boolean;
	current?: boolean;
	selected?: boolean;
};

const createEmptyHistoryEntry = (): PanelHistoryEntry => ({
	newlyAddedPanels: [],
	deletedPanels: [],
	currentMovablePanels: [],
	selectedMovablePanels: []
});

const ensureHistoryInitialized = (data: PanelSlice) => {
	if (data.currentIndex === 0 && data.panelHistory.length === 0) {
		data.panelHistory.push(createEmptyHistoryEntry());
	}
};

const hasHistoryChanged = (
	current: PanelHistoryEntry,
	next: PanelHistoryEntry,
	compare: HistoryCompare
) => (
	(!!compare.newly && current.newlyAddedPanels !== next.newlyAddedPanels) ||
	(!!compare.deleted && current.deletedPanels !== next.deletedPanels) ||
	(!!compare.current && current.currentMovablePanels !== next.currentMovablePanels) ||
	(!!compare.selected && current.selectedMovablePanels !== next.selectedMovablePanels)
);

const pushHistoryIfChanged = (
	data: PanelSlice,
	next: PanelHistoryEntry,
	compare: HistoryCompare
) => {
	const current = data.panelHistory[data.currentIndex];
	if (!current) return;
	if (hasHistoryChanged(current, next, compare)) {
		data.panelHistory = [
			...data.panelHistory.slice(0, data.currentIndex + 1),
			next,
		];
		data.currentIndex = data.panelHistory.length - 1;
	}
};

const pushHistory = (data: PanelSlice, next: PanelHistoryEntry) => {
	data.panelHistory = [
		...data.panelHistory.slice(0, data.currentIndex + 1),
		next,
	];
	data.currentIndex = data.panelHistory.length - 1;
};

export const PanelStateSlice = createSlice({
	name: 'PanelSlice',
	initialState,
	reducers: {
		setAddPanel: (state, action: PayloadAction<{ shouldEnable: boolean }>) => {
			state.data.enableAddPanel = action.payload.shouldEnable;
			if (!action.payload.shouldEnable) {
				state.data.newlyAddedPanels = [];
				state.data.activePanelMode = false;
				state.data.currentIndex = 0;
				state.data.panelHistory = [];
				state.data.enableMultipleAddPanel = false;
			} else {
				state.data.activePanelMode = true;
			}
		},
		setAddMultiplePanel: (state, action: PayloadAction<{ shouldEnable: boolean }>) => {
			state.data.enableMultipleAddPanel = action.payload.shouldEnable;
		},
		setNewlyAddedPanel: (state, action: PayloadAction<{ addedPanels: NewlyAddedPanel[] }>) => {
			const currentPanels = [...state.data.newlyAddedPanels, ...action.payload.addedPanels];

			ensureHistoryInitialized(state.data);

			const newState = {
				newlyAddedPanels: currentPanels,
				deletedPanels: state.data.deletedPanels,
				currentMovablePanels: state.data.currentMovablePanels,
				selectedMovablePanels: state.data.selectedMovablePanels,
			};

			pushHistoryIfChanged(state.data, newState, {
				newly: true,
				current: true,
				deleted: true
			});
			state.data.newlyAddedPanels = currentPanels;
		},
		setTempPanelsGroup: (
			state,
			action: PayloadAction<{ addedPanels: Array<NewlyAddedPanel> }>
		) => {
			state.data.tempPanelsGroup = action.payload.addedPanels;
		},
		setPanelMove: (state, action: PayloadAction<{ shouldEnablePanelMove: boolean }>) => {
			state.data.enablePanelMove = action.payload.shouldEnablePanelMove;
			if (!action.payload.shouldEnablePanelMove) {
				state.data.currentMovablePanels = [];
				state.data.selectedMovablePanels = [];
				state.data.deletedPanels = [];
				state.data.currentIndex = 0;
				state.data.panelHistory = [];
				state.data.activePanelMode = false;
				state.data.isInvalidPanelOperation = false;
			} else {
				state.data.activePanelMode = true;
			}
		},
		setCurrentMovablePanels: (
			state,
			action: PayloadAction<{ currentMovablePanelArr: CurrentMovablePanel[], updateHistory: boolean }>
		) => {
			const {currentMovablePanelArr: incomingPanels, updateHistory} = action.payload;

			const existingPanels = state.data.currentMovablePanels.filter(
				(p) => !incomingPanels.some((incoming) => incoming.panelId === p.panelId)
			);

			const updatedPanels = [...existingPanels, ...incomingPanels];

			if (updateHistory) {
				ensureHistoryInitialized(state.data);
			}

			const newState = {
				newlyAddedPanels: state.data.newlyAddedPanels,
				deletedPanels: state.data.deletedPanels,
				currentMovablePanels: updatedPanels,
				selectedMovablePanels: [],
				isInvalidPanelOperation: false,
			};

			state.data.selectedMovablePanels = [];
			state.data.isInvalidPanelOperation = false;

			if (updateHistory) {
				pushHistoryIfChanged(state.data, newState, {
					newly: true,
					current: true,
					deleted: true
				});
			}

			state.data.currentMovablePanels = updatedPanels;
		},
		setDeletedPanels: (state, action: PayloadAction<{ data: DeletedPanel[] }>) => {
			const { data: panelData } = action.payload;
			const currentDeletedPanels = [...state.data.deletedPanels, ...panelData];

			ensureHistoryInitialized(state.data);

			const newState = {
				newlyAddedPanels: state.data.newlyAddedPanels,
				deletedPanels: currentDeletedPanels,
				currentMovablePanels: state.data.currentMovablePanels,
				selectedMovablePanels: state.data.selectedMovablePanels,
			};

			pushHistoryIfChanged(state.data, newState, {
				newly: true,
				current: true,
				deleted: true
			});

			state.data.deletedPanels = currentDeletedPanels;
		},
		setDeleteIndividualPanelsMode: (state, action: PayloadAction<{ enabled: boolean }>) => {
			state.data.deleteIndividualPanelMode = action.payload.enabled;
			if (!action.payload.enabled) {
				state.data.deletedPanels = [];
				state.data.activePanelMode = false;
				state.data.currentIndex = 0;
				state.data.panelHistory = [];
				state.data.deleteArrayMode = false;
			} else {
				state.data.activePanelMode = true;
			}
		},
		setDeleteArrayMode: (state, action: PayloadAction<{ enabled: boolean }>) => {
			state.data.deleteArrayMode = action.payload.enabled;
		},
		applyMovedPanelsResult: (
			state,
			action: PayloadAction<{
				movedPanels: {
					orientation: string;
					originSegmentId: string;
					exteriorCoords: number[][];
					panelId: string;
					roofSegmentId: string;
				}[],
				deletedPanels: DeletedPanel[]
			}>
		) => {
			const { movedPanels, deletedPanels } = action.payload;

			const updatedMovedPanels = [
				...state.data.currentMovablePanels.filter(p => !movedPanels.some(m => m.panelId === p.panelId)),
				...movedPanels
			];

			const updatedDeletedPanels = [
				...state.data.deletedPanels.filter(p => !deletedPanels.some(d => d.panelId === p.panelId)),
				...deletedPanels
			];

			ensureHistoryInitialized(state.data);

			const newState = {
				newlyAddedPanels: state.data.newlyAddedPanels,
				deletedPanels: updatedDeletedPanels,
				currentMovablePanels: updatedMovedPanels,
				selectedMovablePanels: state.data.selectedMovablePanels,
			};

			pushHistoryIfChanged(state.data, newState, {
				newly: true,
				current: true,
				deleted: true
			});
			state.data.deletedPanels = updatedDeletedPanels;
			state.data.currentMovablePanels = updatedMovedPanels;

		},
		updateMovedPanelsPosition: (
			state,
			action: PayloadAction<{
				movedPanels: {
					orientation: string;
					originSegmentId: string;
					exteriorCoords: number[][];
					panelId: string;
					roofSegmentId: string;
					isValid: boolean;
				}[],
				isInvalidOperation: boolean;
			}>
		) => {
			const { movedPanels, isInvalidOperation } = action.payload;

			state.data.isInvalidPanelOperation = isInvalidOperation;

			const updatedCurrentMovablePanels = state.data.currentMovablePanels.map((p) => {
				const moved = movedPanels.find((m) => m.panelId === p.panelId);
				if (moved) {
					return {
						...p,
						exteriorCoords: moved.exteriorCoords,
						roofSegmentId: moved.roofSegmentId,
						originSegmentId: p.originSegmentId
					};
				}
				return p;
			});

			const updatedSelectedMovablePanels = state.data.selectedMovablePanels.map((p) => {
				const moved = movedPanels.find((m) => m.panelId === p.id);
				if (moved) {
					return {
						...p,
						exteriorCoords: moved.exteriorCoords,
						segmentId: moved.roofSegmentId,
						isValid: moved.isValid,
						originSegmentId: p.originSegmentId
					};
				}
				return p;
			});

			ensureHistoryInitialized(state.data);

			const newState = {
				newlyAddedPanels: state.data.newlyAddedPanels,
				deletedPanels: state.data.deletedPanels,
				currentMovablePanels: updatedCurrentMovablePanels,
				selectedMovablePanels: updatedSelectedMovablePanels
			};

			pushHistoryIfChanged(state.data, newState, {
				newly: true,
				current: true,
				deleted: true,
				selected: true
			});

			state.data.currentMovablePanels = updatedCurrentMovablePanels;
			state.data.selectedMovablePanels = updatedSelectedMovablePanels;
		},
		undoPanelState: (state) => {
			if (state.data.currentIndex > 0) {
				state.data.currentIndex -= 1;
				const previousState = state.data.panelHistory[state.data.currentIndex] as any;
				state.data.newlyAddedPanels = previousState.newlyAddedPanels;
				state.data.currentMovablePanels = previousState.currentMovablePanels;
				state.data.deletedPanels = previousState.deletedPanels;
				state.data.selectedMovablePanels = previousState.selectedMovablePanels || [];
				state.data.isInvalidPanelOperation = state.data.selectedMovablePanels.some((p: any) => p.isValid === false);
			}
		},
		redoPanelState: (state) => {
			if (state.data.currentIndex < state.data.panelHistory.length - 1) {
				state.data.currentIndex += 1;
				const nextState = state.data.panelHistory[state.data.currentIndex] as any;
				state.data.newlyAddedPanels = nextState.newlyAddedPanels;
				state.data.currentMovablePanels = nextState.currentMovablePanels;
				state.data.deletedPanels = nextState.deletedPanels;
				state.data.selectedMovablePanels = nextState.selectedMovablePanels || [];
				state.data.isInvalidPanelOperation = state.data.selectedMovablePanels.some((p: any) => p.isValid === false);
			}
		},
		togglePanelModal: (state, action: PayloadAction<{ targetModal: string, val: boolean }>) => {
			switch (action.payload.targetModal) {
			case 'CANCEL_ADD_PANEL':
				state.data.cancelAddPanelModal = action.payload.val;
				break;
			case 'CANCEL_MOVE_PANEL':
				state.data.cancelMovePanelModal = action.payload.val;
				break;
			case 'CANCEL_DELETE_PANEL':
				state.data.cancelDeletePanelModal = action.payload.val;
				break;
			}
		},
		setAddSelectedMovablePanel: (state, action: PayloadAction<RasterSolarPanel | RasterSolarPanel[]>) => {
			const selectedPanel = Array.isArray(action.payload) ? action.payload : [action.payload];

			selectedPanel.forEach((panel) => {
				const index = state.data.selectedMovablePanels.findIndex(p => p.id === panel.id);
				if (index > -1) {
					state.data.selectedMovablePanels.splice(index, 1);
				} else {
					const movedVersion = state.data.currentMovablePanels.find(mp => mp.panelId === panel.id);

					const panelToAdd = movedVersion ? {
						...panel,
						exteriorCoords: movedVersion.exteriorCoords,
						segmentId: movedVersion.roofSegmentId,
						orientation: movedVersion.orientation as any,
						originSegmentId: movedVersion.originSegmentId
					} : {
						...panel,
						originSegmentId: panel.segmentId
					};

					state.data.selectedMovablePanels.push(panelToAdd);
				}
			});
			ensureHistoryInitialized(state.data);

			const newState = {
				newlyAddedPanels: state.data.newlyAddedPanels,
				deletedPanels: state.data.deletedPanels,
				currentMovablePanels: state.data.currentMovablePanels,
				selectedMovablePanels: [...state.data.selectedMovablePanels]
			};

			pushHistory(state.data, newState);
		},
		setToggleAddSelectedMovablePanel: (state, action: PayloadAction<{ panelId: string }>) => {
			const { panelId } = action.payload;
			const index = state.data.selectedMovablePanels.findIndex(p => p.id === panelId);
			if (index > -1) {
				const [removedPanel] = state.data.selectedMovablePanels.splice(index, 1);
				if (state.data.selectedMovablePanels.length === 0) {
					state.data.isInvalidPanelOperation = false;
				}
				if (removedPanel) {
					const updatedPanels = [
						...state.data.currentMovablePanels.filter(
							(p) => p.panelId !== removedPanel.id
						),
						{
							panelId: removedPanel.id,
							originSegmentId: removedPanel.originSegmentId,
							roofSegmentId: removedPanel.segmentId,
							orientation: removedPanel.orientation,
							exteriorCoords: removedPanel.exteriorCoords,
						},
					];
					state.data.currentMovablePanels = updatedPanels;
					if (state.data.currentIndex === 0 && state.data.panelHistory.length === 0) {
						state.data.panelHistory.push({
							newlyAddedPanels: [],
							deletedPanels: [],
							currentMovablePanels: [],
							selectedMovablePanels: [],
						});
					}

					const newState = {
						newlyAddedPanels: state.data.newlyAddedPanels,
						deletedPanels: state.data.deletedPanels,
						currentMovablePanels: updatedPanels,
						selectedMovablePanels: [...state.data.selectedMovablePanels],
					};
					const currentHistory = state.data.panelHistory[state.data.currentIndex];

					if (
						currentHistory?.currentMovablePanels !== updatedPanels ||
						currentHistory?.selectedMovablePanels !== state.data.selectedMovablePanels
					) {
						state.data.panelHistory = [
							...state.data.panelHistory.slice(0, state.data.currentIndex + 1),
							newState,
						];
						state.data.currentIndex = state.data.panelHistory.length - 1;
					}

				}
			}
		},
		setClearSelectedMovablePanels: (state) => {
			if (state.data.selectedMovablePanels.length > 0 && !state.data.isInvalidPanelOperation) {
				const panelsToSettle = state.data.selectedMovablePanels.map(p => ({
					panelId: p.id,
					originSegmentId: p.originSegmentId,
					roofSegmentId: p.segmentId,
					orientation: p.orientation,
					exteriorCoords: p.exteriorCoords,
				}));

				state.data.currentMovablePanels = [
					...state.data.currentMovablePanels.filter(p => !panelsToSettle.some(s => s.panelId === p.panelId)),
					...panelsToSettle
				];
			}
			// runs on every pointer down off a panel; a fresh [] when already empty re-rendered every panel
			if (state.data.selectedMovablePanels.length > 0) {
				state.data.selectedMovablePanels = [];
			}
			state.data.isInvalidPanelOperation = false;
		},
		setInvalidOperation: (state, action: PayloadAction<{ isInvalidOperation: boolean }>) => {
			state.data.isInvalidPanelOperation = action.payload.isInvalidOperation;
		}
	}
});

export const {
	setAddPanel, setNewlyAddedPanel,
	setPanelMove, setCurrentMovablePanels,
	setDeletedPanels, setDeleteIndividualPanelsMode,
	undoPanelState, redoPanelState, togglePanelModal,
	setAddMultiplePanel, setTempPanelsGroup, setDeleteArrayMode,
	setAddSelectedMovablePanel, setClearSelectedMovablePanels,
	applyMovedPanelsResult, updateMovedPanelsPosition,
	setInvalidOperation, setToggleAddSelectedMovablePanel
} = PanelStateSlice.actions;

export default PanelStateSlice.reducer;
